import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../api/supabase';
import { sessionService, getStatus } from '../auth/sessionService';
import { errorHandler } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import { patchConsent } from '../api/user';
import { consumePendingConsent } from '../utils/legal';
import SessionTimeoutModal from './SessionTimeoutModal.jsx';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const AUTH_SETTLE_MS = 2200;

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authSettling, setAuthSettling] = useState(false);
    const settleTimerRef = useRef(null);
    const [sessionTimeoutModal, setSessionTimeoutModal] = useState({
        isVisible: false,
        remainingTime: 60
    });

    const [sessionCheckInterval, setSessionCheckInterval] = useState(null);
    const [timeoutWarningInterval, setTimeoutWarningInterval] = useState(null);

    const beginAuthSettle = useCallback(() => {
        setAuthSettling(true);
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(() => {
            setAuthSettling(false);
            settleTimerRef.current = null;
        }, AUTH_SETTLE_MS);
    }, []);

    useEffect(() => () => {
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    }, []);

    const getInitialSession = async () => {
        try {
            const session = await sessionService.getSession();
            if (session) {
                setIsLoggedIn(true);
                setUser(session.user);
            }
        } catch (error) {
            const handledError = errorHandler.handleError(error, { action: 'get_initial_session' });
        } finally {
            setLoading(false);
        }
    };

    const startSessionMonitoring = () => {
        const sessionInterval = setInterval(async () => {
            if (isLoggedIn) {
                const status = await getStatus();
                
                if (status.needsRefresh) {
                    try {
                        await sessionService.refresh();
                    } catch (e) {
                        await logout();
                    }
                }
                
                if (status.isExpired) {
                    await logout();
                }
            }
        }, 10000);
        
        setSessionCheckInterval(sessionInterval);

        const warningInterval = setInterval(async () => {
            if (isLoggedIn) {
                const status = await getStatus();
                
                if (status.isExpiringSoon && !sessionTimeoutModal.isVisible) {
                    setSessionTimeoutModal({
                        isVisible: true,
                        remainingTime: status.remainingTime
                    });
                }
            }
        }, 10000);
        
        setTimeoutWarningInterval(warningInterval);
    };

    const stopSessionMonitoring = () => {
        if (sessionCheckInterval) {
            clearInterval(sessionCheckInterval);
            setSessionCheckInterval(null);
        }
        if (timeoutWarningInterval) {
            clearInterval(timeoutWarningInterval);
            setTimeoutWarningInterval(null);
        }
    };

    const handleSessionExtension = async () => {
        try {
            await refreshToken();
            setSessionTimeoutModal({ isVisible: false, remainingTime: 60 });
        } catch (error) {
            const handledError = errorHandler.handleError(error, { action: 'session_extension' });
            await logout();
        }
    };

    const handleSessionTimeout = async () => {
        try {
            await logout();
            setSessionTimeoutModal({ isVisible: false, remainingTime: 60 });
        } catch (error) {
            const handledError = errorHandler.handleError(error, { action: 'session_timeout_logout' });
        }
    };

    useEffect(() => {
        const handleOAuthCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const error = urlParams.get('error');
            const errorDescription = urlParams.get('error_description');
            
            if (code || error) {
                setLoading(true);

                if (error) {
                    window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                    setLoading(false);
                    return false;
                }
                
                if (code) {
                    try {
                        if (supabase.auth.exchangeCodeForSession) {
                            const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
                            
                            if (exchangeError) {
                                window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                                setLoading(false);
                                return false;
                            }
                            
                            if (data && data.session) {
                                await new Promise(resolve => setTimeout(resolve, 200));
                                
                                const { data: { session: verifiedSession } } = await supabase.auth.getSession();
                                if (verifiedSession) {
                                    setIsLoggedIn(true);
                                    setUser(verifiedSession.user);
                                    beginAuthSettle();
                                    window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                                    navigate('/', { replace: true });
                                    setLoading(false);
                                    return true;
                                } else {
                                    setIsLoggedIn(true);
                                    setUser(data.session.user);
                                    beginAuthSettle();
                                    window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                                    navigate('/', { replace: true });
                                    setLoading(false);
                                    return true;
                                }
                            }
                        }
                        
                        await new Promise(resolve => setTimeout(resolve, 500));
                        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                        
                        if (sessionError) {
                            setLoading(false);
                        } else if (session) {
                            setIsLoggedIn(true);
                            setUser(session.user);
                            beginAuthSettle();
                            window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                            navigate('/', { replace: true });
                            setLoading(false);
                            return true;
                        } else {
                            window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                            setLoading(false);
                        }
                    } catch (err) {
                        window.history.replaceState({}, '', window.location.origin + window.location.pathname + '#/');
                        setLoading(false);
                    }
                }
            }
            return false;
        };
        
        (async () => {
            const oauthProcessed = await handleOAuthCallback();
            if (oauthProcessed) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            await getInitialSession();
        })();
        
        const handleOAuthComplete = (event, data) => {
            setTimeout(async () => {
                const session = await sessionService.getSession();
                if (session) {
                    setIsLoggedIn(true);
                    setUser(session.user);
                }
            }, 1000);
        };
        
        if (window.electronAPI && window.electronAPI.onOAuthComplete) {
            window.electronAPI.onOAuthComplete(handleOAuthComplete);
        }
        
        return () => {
            if (window.electronAPI && window.electronAPI.removeOAuthListener) {
                window.electronAPI.removeOAuthListener(handleOAuthComplete);
            }
        };
    }, [navigate, beginAuthSettle]);

    useEffect(() => {
        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (session) {
                setIsLoggedIn(true);
                setUser(session.user);
                if (event === 'SIGNED_IN') {
                    beginAuthSettle();
                    const pendingConsent = consumePendingConsent();
                    if (pendingConsent) {
                        patchConsent(pendingConsent).catch(() => {});
                    }
                    console.log('User is logged in');
                }
                setLoading(false);
                
                const urlParams = new URLSearchParams(window.location.search);
                const hasOAuthParams = urlParams.has('code') || urlParams.has('access_token') || urlParams.has('error');
                
                if (event === 'SIGNED_IN') {
                    if (hasOAuthParams) {
                        const cleanUrl = window.location.origin + window.location.pathname + '#/';
                        window.history.replaceState({}, '', cleanUrl);
                    }
                    
                    const { data: { session: verifySession } } = await supabase.auth.getSession();
                    if (verifySession) {
                        setIsLoggedIn(true);
                        setUser(verifySession.user);
                        setLoading(false);
                    }
                    
                    const currentHash = window.location.hash;
                    if (!currentHash || currentHash === '#' || currentHash === '#/') {
                        setTimeout(() => {
                            navigate('/', { replace: true });
                        }, 100);
                    }
                }
            } else {
                setIsLoggedIn(false);
                setUser(null);
                setLoading(false);
            }
        });
        return () => {
            try { data.subscription.unsubscribe(); } catch (_) {}
        };
    }, [navigate, beginAuthSettle]);

    useEffect(() => {
        const handleSessionCheck = async () => {
            if (isLoggedIn) {
                const status = await getStatus();
                
                if (status.isExpired) {
                    await logout();
                } else if (status.needsRefresh) {
                    try {
                        await sessionService.refresh();
                    } catch (e) {
                        await logout();
                    }
                }
            }
        };

        window.addEventListener('checkSessionStatus', handleSessionCheck);
        
        return () => {
            window.removeEventListener('checkSessionStatus', handleSessionCheck);
        };
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn) {
            startSessionMonitoring();
        } else {
            stopSessionMonitoring();
        }

        return () => {
            stopSessionMonitoring();
        };
    }, [isLoggedIn]);

    const logout = async () => {
        console.log('AuthContext: Starting logout...');
        
        // Clear state immediately (don't wait for async operations)
        setIsLoggedIn(false);
        setUser(null);
        
        // Clear localStorage immediately
        localStorage.removeItem('temp_access_token');
        localStorage.removeItem('encrypted_access_token');
        localStorage.removeItem('encrypted_refresh_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('token_type');
        localStorage.removeItem('oauth_completed');
        localStorage.removeItem('csrf_token');
        
        // Start signOut in background (don't wait for it)
        console.log('AuthContext: Calling supabase.auth.signOut() (non-blocking)...');
        supabase.auth.signOut()
            .then(({ error }) => {
                if (error) {
                    console.error('AuthContext: SignOut error (background):', error);
                } else {
                    console.log('AuthContext: SignOut completed (background)');
                }
            })
            .catch((error) => {
                console.warn('AuthContext: SignOut promise rejected (background):', error);
            });
        
        // Don't wait for signOut - it can complete in the background
        // The state is already cleared, so the user is effectively logged out
        console.log('AuthContext: Logout state cleared, signOut running in background');
    };

    const testSessionTimeout = () => {
        setSessionTimeoutModal({
            isVisible: true,
            remainingTime: 15
        });
    };

    const value = {
        user,
        isLoggedIn,
        loading,
        authSettling,
        isAuthBusy: loading || authSettling,
        logout,
        handleSessionExtension,
        handleSessionTimeout,
        testSessionTimeout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <SessionTimeoutModal
                isVisible={sessionTimeoutModal.isVisible}
                onExtend={handleSessionExtension}
                onLogout={handleSessionTimeout}
                remainingTime={sessionTimeoutModal.remainingTime}
            />
        </AuthContext.Provider>
    );
}; 